import styled, { css } from 'styled-components';

type MismatchSeverity = 'warning' | 'error';

export const Container = styled.div`
  max-width: 1280px;
  padding: 0.5rem 1rem 0.5rem;

  @media ${({ theme }) => theme.device.mobile} {
    padding: 1.5rem;
  }

  .income-verification-icons {
    svg {
      width: 37px !important;
      height: 37px !important;
    }
    width: 37px;
  }

  .tile-form-fields form {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    margin-top: 0.75rem;
    margin-bottom: 0.75rem;

    /* InputText's styled Container has margin-bottom: 1.7rem. Zero it
       out so the form's gap property controls all row spacing uniformly.
       The Container renders with aria-invalid, which is a reliable hook. */
    div[aria-invalid] {
      margin-bottom: 0;
    }
  }
`;

export const MismatchTileWrapper = styled.div<{
  severity?: MismatchSeverity;
  isExcluded?: boolean;
}>`
  position: relative;

  ${({ severity }) =>
    severity === 'error' &&
    css`
      border: 2px solid ${({ theme }) => theme.color.red[600]};
      background: ${({ theme }) => theme.color.red[100]};
      border-radius: 8px;
      padding: 1rem;
    `}

  ${({ severity }) =>
    severity === 'warning' &&
    css`
      border: 2px solid ${({ theme }) => theme.color.yellow[700]};
      background: ${({ theme }) => theme.color.yellow[100]};
      border-radius: 8px;
      padding: 1rem;
    `}

  ${({ isExcluded }) =>
    isExcluded &&
    css`
      opacity: 0.55;
      .tile-form-fields {
        pointer-events: none;
      }
    `}
`;

export const StatusBadge = styled.span<{
  variant: 'excluded-error' | 'excluded-warning' | 'connected' | 'included-override' | 'automated';
}>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  white-space: nowrap;

  ${({ variant }) =>
    variant === 'excluded-error' &&
    css`
      background: ${({ theme }) => theme.color.red[100]};
      color: ${({ theme }) => theme.color.red[700]};
    `}

  ${({ variant }) =>
    variant === 'excluded-warning' &&
    css`
      background: ${({ theme }) => theme.color.yellow[100]};
      color: ${({ theme }) => theme.color.yellow[500]};
    `}

  ${({ variant }) =>
    variant === 'connected' &&
    css`
      background: ${({ theme }) => theme.color.purple[100]};
      color: ${({ theme }) => theme.color.purple[700]};
      margin-top: 6px;
      margin-bottom: 6px;
    `}

  ${({ variant }) =>
    variant === 'included-override' &&
    css`
      background: ${({ theme }) => theme.color.green[100]};
      color: ${({ theme }) => theme.color.green[700]};
    `}

  ${({ variant }) =>
    variant === 'automated' &&
    css`
      background: ${({ theme }) => theme.color.blue[100]};
      color: ${({ theme }) => theme.color.blue[700]};
      margin-top: 6px;
      margin-bottom: 6px;
    `}
`;

export const MismatchDetailRow = styled.div<{ severity: MismatchSeverity }>`
  display: flex;
  align-items: center;
  gap: 1.5rem;
  padding: 8px 12px;
  border-radius: 4px;
  margin-top: 0.75rem;
  margin-bottom: 0.5rem;
  font-size: 0.85rem;

  ${({ severity }) =>
    severity === 'error' &&
    css`
      background: ${({ theme }) => theme.color.red[100]};
      color: ${({ theme }) => theme.color.red[700]};
    `}

  ${({ severity }) =>
    severity === 'warning' &&
    css`
      background: ${({ theme }) => theme.color.yellow[100]};
      color: ${({ theme }) => theme.color.yellow[500]};
    `}
`;

export const MismatchInputWrapper = styled.div<{ severity: MismatchSeverity }>`
  border: 2px solid
    ${({ severity, theme }) =>
      severity === 'error' ? theme.color.red[600] : theme.color.yellow[700]};
  border-radius: 6px;
  background: ${({ severity, theme }) =>
    severity === 'error' ? theme.color.red[100] : theme.color.yellow[100]};
`;

export const TileIncludeRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding-top: 0.5rem;
  margin-top: 0.25rem;

  label {
    font-size: 0.875rem;
    font-weight: 500;
    cursor: pointer;
  }
`;
